import React, {
  PureComponent,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { ErrorBoundary } from "react-error-boundary";

import { Form, FormSpy } from "react-final-form";

import { useRecoilValue } from "recoil";
import {
  tableErrorsSelector,
  tableValidSelector,
} from "./Atoms/Table/selectors";

import Page from "./Page";
import PageNavigation from "./PageNavigation";
import PageTitle from "./UI/PageTitle";
import FormErrors from "./UI/FormErrors";
import FormSummary from "./FormSummary";
import classNames from "classnames";
import decorators from "./Decorators/decorators";
import arrayMutators from "final-form-arrays";
import setFieldData from "final-form-set-field-data";
import setFieldTouched from "final-form-set-field-touched";
import FormStatusComponent from "../FormStatusComponent";
import FormActions from "./FormActions";
import { withRouter } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import allowSubmission from "./SubmissionUtilities/allowSubmission";
import visitedAllPages from "./SubmissionUtilities/visitedAllPages";
import developerRoleExists from "./SubmissionUtilities/isDeveloper";
import testerRoleExists from "./SubmissionUtilities/isTester";
import formTypeAvailable from "./SubmissionUtilities/formTypeAvailable";
import FormWarnings from "./UI/FormWarnings";
import { useSetFocus } from "../Hooks/useSetFocus";
import { deepEqual } from "../Utils/deepEqual";

function ErrorFallbackForm({ error, resetErrorBoundary }) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  );
}

function MultiPage(props) {
  const user = useSelector((state) => state.appReducer.userDataReducer.data);
  // Make forms keyboard navigable
  const [formRef, setFormFocus] = useSetFocus();
  const [navRef, setNavFocus] = useSetFocus();
  const {
    uiSchema,
    uiSchema_meta,
    uiSchema_nav,
    initialValues,
    profiles,
    counties,
    toggleModal,
    handleSubmit: simpleSubmit,
    history,
    closeModal,
    formExit,
    renderFormExit,
    renderFormActions,
    form_ui,
    form_ui_name,
    tablePagesErrors,
    formSaveMutation,
    formSaveMutationCallback,
  } = props;
  let [page, setPage] = useState(0);
  let [values, setvalues] = useState(props.initialValues || {});
  let [pages_visited, setPages_visited] = useState(
    (props.form_ui && props.form_ui.pages_visited) || []
  );
  let [pages_errors, setPages_errors] = useState(
    (props.form_ui && props.form_ui.pages_errors) || []
  );
  let [uiSchemaMutation, setUiSchemaMutation] = useState([]);
  let [summary_loading, setSummary_loading] = useState(false);

  const formApiRef = useRef(null);
  const { isLocked, lockedPage, handlePageLock } = { ...props.pageLock };

  // Seems like dead code and only breaks things. Leaving in to minimize impact, look into later and see if this is even necessary.
  useEffect(() => {
    if (props.returningToForm) {
      // Possible it originally used what is now called "current_values"
      Object.keys(values).forEach((key) => {
        formApiRef.current.mutators.setFieldTouched(key, true);
      });
    }
  }, []);

  const memoMutators = useMemo(() => {
    return {
      ...arrayMutators,
      setFieldData,
      setFieldTouched,
      ...uiSchemaMutation,
    };
  }, []);

  useEffect(() => {
    let uiSchemaMutation = [];
    props.uiSchema.map((pages, i) => {
      if (pages.mutations) {
        uiSchemaMutation = { ...uiSchemaMutation, ...pages.mutations };
      }
    });

    if (props.validateOnLoad) {
      props.validateOnLoad.forEach((item) => {
        const fields = item?.if?.fields || [];
        let updateForm = false;
        switch (item.if.condition) {
          case "AND":
            const count = fields.length;
            let tally = 0;
            fields.forEach((field) => {
              if (field.hasOwnProperty("is")) {
                if (
                  values &&
                  values[field.field] &&
                  values[field.field] === field.is
                ) {
                  tally++;
                }
              }
              if (field.hasOwnProperty("isNot")) {
                if (values && values[field] && values[field] === field.is) {
                  tally++;
                }
              }
            });
            updateForm = count == tally;
            break;
          default:
        }
        if (updateForm) {
          if (item.then.setPageVisited) {
            addPageVisited(item.then.setPageVisited);
          }
          if (item.then.setPageErrored) {
            addPageErrored(item.then.setPageErrored);
            updatePagesErrored(item.then.setPageErrored, true);
          }
        }
      });
    }

    setUiSchemaMutation(uiSchemaMutation);
  }, []);

  const handleSetPage = (nextPage) => {
    let isNextPageLocked =
      typeof lockedPage === "number" &&
      lockedPage < nextPage &&
      isLocked?.current === true;
    if (isNextPageLocked) {
      return;
    }
    setPage(nextPage);
  };

  const addPageVisited = (page) => {
    setPages_visited([...pages_visited, page]);
  };
  const addPageErrored = (page) => {
    setPages_errors([...pages_errors, page]);
  };
  const removePageVisited = (page) => {
    const newPagesVisited = pages_visited.filter(function (item) {
      return item !== page;
    });
    setPages_visited(newPagesVisited);
  };
  const removePageErrored = (page) => {
    const newPagesErrored = pages_errors.filter(function (item) {
      return item !== page;
    });
    setPages_errors(newPagesErrored);
  };

  const next = (testExtraAction) => {
    let _pages_visited = [...pages_visited];
    let _page = page;
    if (testExtraAction) {
      testExtraAction();
    }
    // append current page to visited list before proceeding
    if (pages_visited.indexOf(page) === -1) {
      _pages_visited = [..._pages_visited, page];
    }
    setPages_visited(_pages_visited);
    handleSetPage(Math.min(_page + 1, props.uiSchema.length));
    document.getElementsByClassName(
      "ReactModal__Content--after-open"
    )[0].scrollTop = 0;
  };

  const previous = (testExtraAction) => {
    let _pages_visited = [...pages_visited];

    if (testExtraAction) {
      testExtraAction();
    }
    // append current page to visited list before going back
    if (pages_visited.indexOf(page) === -1) {
      _pages_visited = [..._pages_visited, page];
    }
    setPages_visited(_pages_visited);
    handleSetPage(Math.max(page - 1, 0));
    document.getElementsByClassName(
      "ReactModal__Content--after-open"
    )[0].scrollTop = 0;
  };

  const setPageNav = (newPage) => {
    let _pages_visited = [...pages_visited];

    // append current page to visited list
    if (pages_visited.indexOf(page) === -1) {
      _pages_visited = [..._pages_visited, page];
    }
    setPages_visited(_pages_visited);
    handleSetPage(newPage);
  };

  const goSummaryPage = () => {
    handleSetPage(props.uiSchema.length);
  };

  const setSummaryLoading = (bool) => {
    if (isLocked?.current === true) {
      return setSummary_loading(false);
    }
    setSummary_loading(bool);
  };

  const updatePagesErrored = (page, hasError) => {
    let _pages_errors = pages_errors.slice(0);
    const page_index = _pages_errors.indexOf(page);
    const page_error_exists = page_index > -1;

    // append current page to visited list before proceeding
    if (hasError && !page_error_exists) {
      _pages_errors = _pages_errors.concat(page);
    } else if (!hasError && page_error_exists) {
      _pages_errors.splice(page_index, 1);
    }

    let updated = !_pages_errors.compare(pages_errors);

    if (updated) {
      setPages_errors(_pages_errors);
    }
  };

  const renderPages = (formApi) => {
    const {
      inputResolver,
      uiSchema,
      profiles,
      counties,
      disableValidation,
      form_ui_name,
      reuseMetadata,
    } = props;
    let ordered_pages_visited =
      pages_visited && [...pages_visited].sort((a, b) => a - b);
    ordered_pages_visited = ordered_pages_visited?.filter(
      (o) => o !== uiSchema.length
    );
    if (ordered_pages_visited !== undefined) {
      return (
        <FormSpy subscription={{ values: true }}>
          {({ values: formValues }) =>
            ordered_pages_visited.map((visited_page) => {
              return (
                <Page
                  isLastPage={true}
                  key={visited_page}
                  page_index={visited_page}
                  active={false}
                  uiSchema={uiSchema[visited_page]}
                  form_ui_name={form_ui_name}
                  formApi={formApi}
                  formValues={formValues}
                  history={history}
                  inputResolver={inputResolver}
                  returningToForm={true}
                  multiPage={true}
                  profiles={profiles}
                  counties={counties}
                  disableValidation={disableValidation}
                  addPageVisited={addPageVisited}
                  addPageErrored={addPageErrored}
                  removePageVisited={removePageVisited}
                  removePageErrored={removePageErrored}
                  pages_errors={pages_errors}
                  reuseMetadata={reuseMetadata}
                />
              );
            })
          }
        </FormSpy>
      );
    } else {
      return [];
    }
  };

  const renderIncompletePageMessages = () => {
    const { uiSchema } = props;

    let incomplete_pages = [...Array(uiSchema.length).keys()];
    incomplete_pages = incomplete_pages.filter(
      (o) => pages_visited.indexOf(o) === -1
    );

    const incompleteSummaryStyle = {
      backgroundColor: "#faf3d1",
      border: "1px solid #ffbe2e",
      padding: "10px",
      marginTop: "1rem",
      fontSize: "14px",
    };

    if (incomplete_pages.length > 0) {
      return (
        <div className="margin-top-5">
          <strong>Incomplete:</strong>
          <div style={incompleteSummaryStyle}>
            {incomplete_pages.map((p) => {
              let page = uiSchema[p];
              return (
                <ul key={page.name}>
                  <li>{page.name}</li>
                </ul>
              );
            })}
          </div>
        </div>
      );
    } else {
      return null;
    }
  };

  const renderPage = (formApi, new_form_ui) => {
    const {
      returningToForm,
      inputResolver,
      uiSchema,
      uiSchema_lookup,
      disableValidation,
      form_ui_name,
      reuseMetadata,
    } = props;
    const { profiles, counties, form_ui } = props;
    const page_number = page;
    const returningToPage = pages_visited.indexOf(page_number) > -1;

    return (
      <FormSpy subscription={{ values: true }}>
        {({ values: formValues }) => (
          <Page
            formRef={formRef}
            active={true}
            formApi={formApi}
            form_ui={new_form_ui}
            form_ui_name={form_ui_name}
            formValues={formValues}
            goToStep={setPage}
            page_index={page_number}
            history={history}
            uiSchema={uiSchema[page_number]}
            uiSchema_lookup={uiSchema_lookup}
            inputResolver={inputResolver}
            returningToForm={returningToForm}
            returningToPage={returningToPage}
            reuseMetadata={reuseMetadata}
            multiPage={true}
            profiles={profiles}
            counties={counties}
            disableValidation={disableValidation}
            addPageVisited={addPageVisited}
            addPageErrored={addPageErrored}
            removePageVisited={removePageVisited}
            removePageErrored={removePageErrored}
          />
        )}
      </FormSpy>
    );
  };

  const renderLastPage = (allPages) => {
    const {
      uiSchema_nav,
      uiSchema_lookup,
      uiSchema,
      form_ui_name,
      displayFormWarnings,
    } = props;
    setSummaryLoading(false);

    const incomplete_pages = [...Array(uiSchema.length).keys()].filter(
      (o) => pages_visited.indexOf(o) === -1
    );
    return (
      <div>
        <PageTitle formRef={formRef}>{"Review & Submit"}</PageTitle>

        {renderIncompletePageMessages()}

        <FormSpy subscription={{ errors: true, values: true }}>
          {({ errors, values }) => {
            errors = { ...errors, ...tableErrors };
            return (
              <>
                <div>
                  {Object.keys(errors).length > 0 && (
                    <div className="margin-top-5">
                      <strong>Submission Errors:</strong>
                      <FormErrors
                        errors={errors}
                        uiSchema_nav={uiSchema_nav}
                        uiSchema_lookup={uiSchema_lookup}
                      />
                    </div>
                  )}
                </div>
                {!!displayFormWarnings &&
                  Object.keys(errors).length === 0 &&
                  incomplete_pages.length === 0 && (
                    <FormWarnings
                      values={values}
                      errors={errors}
                      uiSchema_lookup={uiSchema_lookup}
                      uiSchema_nav={uiSchema_nav}
                      form_ui_name={form_ui_name}
                    />
                  )}
              </>
            );
          }}
        </FormSpy>

        {allPages}
        {!!displayFormWarnings && <div></div>}
        <div className="margin-top-5">
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <div>
                {Object.keys(values).length > 0 && (
                  <div>
                    <FormSummary
                      values={values}
                      uiSchema_nav={uiSchema_nav}
                      uiSchema_lookup={uiSchema_lookup}
                      formName={form_ui_name}
                      displayFormWarnings={displayFormWarnings}
                    />
                  </div>
                )}
              </div>
            )}
          </FormSpy>
        </div>
      </div>
    );
  };

  const tableErrors = useRecoilValue(tableErrorsSelector);
  const isTableValid = useRecoilValue(tableValidSelector);

  const isLastPage = page === uiSchema.length;

  let formContainerClasses = classNames("padding-2", {
    "form-body-container": true,
    "mobile:grid-col-12": true,
    "tablet:grid-col-5": true,
    "desktop:grid-col-5": true,
  });
  return props.formSubmitting === true ? (
    <FormStatusComponent formStatusType="loading" />
  ) : (
    <>
      <PageNavigation
        activePage={page}
        setPage={setPageNav}
        summary_loading={summary_loading}
        setSummaryLoading={setSummaryLoading}
        goSummaryPage={goSummaryPage}
        uiSchema_meta={uiSchema_meta}
        uiSchema_nav={uiSchema_nav}
        pages_errors={pages_errors}
        pages_visited={pages_visited}
        // Make forms keyboard navigable
        keyboardFocusProps={{
          navRef,
          setNavFocus,
          setFormFocus,
        }}
        formSaveMutation={formSaveMutation}
        formSaveMutationCallback={formSaveMutationCallback}
        values={values}
        form_ui={{ pages_visited, pages_errors }}
        tablePagesErrors={tablePagesErrors}
      />
      <div
        id="formContainer"
        className={formContainerClasses}
        // Make forms keyboard navigable
        onBlur={(e) => {
          if (
            // Calls focus to previous nav item (left side) when leaving form container (right side)
            e.relatedTarget?.classList?.contains("page-item")
          ) {
            setNavFocus();
          }
        }}
      >
        <ErrorBoundary
          FallbackComponent={ErrorFallbackForm}
          onReset={() => {
            // reset the state of your app so the error doesn't happen again
          }}
        >
          <Form
            initialValues={initialValues}
            decorators={[...decorators]}
            onSubmit={(values) => simpleSubmit(values)}
            mutators={memoMutators}
            subscription={{
              submitting: true,
              pristine: true,
              invalid: true,
              errors: true,
              values: true,
            }}
          >
            {({
              handleSubmit,
              invalid,
              errors: page_errors,
              submitting,
              form,
              values: current_values,
              ...rest
            }) => {
              formApiRef.current = form;
              // Since we're extracting formApi with useRef, should move handlePageLock to top to keep DRY. But will need useEffect dependencies for it(maybe utilize setvalues)
              if (typeof handlePageLock === "function" && page === lockedPage) {
                handlePageLock(form);
              }

              // Update state only if the values have changed or is not empty
              if (
                !deepEqual(values, current_values) &&
                Object.keys(current_values).length > 0
              ) {
                setvalues(current_values);
              }

              if (!isTableValid) {
                invalid = true;
              }
              let pageHasErrors =
                page_errors && Object.keys(page_errors).length > 0;
              if (pageHasErrors) {
                updatePagesErrored(page, true);
              } else {
                updatePagesErrored(page, false);
              }

              const quit = () => {
                toggleModal();
              };
              const submit = () => {
                simpleSubmit(current_values);
              };

              let allpages = renderPages(form);

              //Check if current user is able to submit this form
              const { FormStatus, Roles } = user;
              let new_form_ui = { pages_visited, pages_errors };
              const application_id = window.location.pathname.split("/")[3];

              let FormTypeAvailable = formTypeAvailable(
                FormStatus,
                application_id
              );

              let VisitedAllPages = visitedAllPages(new_form_ui, uiSchema);

              let isDeveloper = developerRoleExists(Roles);
              let isTester = testerRoleExists(Roles);
              let canSubmit = allowSubmission(
                FormStatus,
                FormTypeAvailable,
                VisitedAllPages,
                isDeveloper,
                isTester
              );

              return (
                <>
                  <form onSubmit={handleSubmit}>
                    {isLastPage
                      ? renderLastPage(allpages)
                      : renderPage(form, new_form_ui)}
                    {/** render custom form actions if specified in SimpleForm, otherwise default set up */}
                    {renderFormActions ? (
                      renderFormActions(
                        isLastPage,
                        page,
                        invalid,
                        submitting,
                        next,
                        previous,
                        quit,
                        current_values,
                        new_form_ui,
                        form_ui_name,
                        canSubmit,
                        submit,
                        tablePagesErrors, // 13
                        isLocked
                      )
                    ) : (
                      <FormActions
                        isLastPage={isLastPage}
                        page={page}
                        invalid={invalid}
                        submitting={submitting}
                        next={next}
                        previous={previous}
                        quit={quit}
                        values={current_values}
                        tablePagesErrors={tablePagesErrors}
                      />
                    )}
                  </form>
                  {formExit &&
                    renderFormExit(
                      current_values,
                      new_form_ui,
                      form_ui_name,
                      tablePagesErrors
                    )}
                </>
              );
            }}
          </Form>
        </ErrorBoundary>
      </div>
    </>
  );
}

export default MultiPage;
